import { useLocation, useNavigate, useParams } from 'react-router'
import { AppShell } from './AppShell'
import { DashboardSwitch } from './live/DashboardSwitch'
import { LiveFindings } from './live/LiveFindings'
import { LiveFindingDetail } from './live/LiveFindingDetail'
import { LiveSettings } from './live/LiveSettings'
import { AI_TAB, AZURE_TAB, settingsTabFor, SETTINGS_TABS } from './live/settingsTabs'
import { LivePlans } from './live/LivePlans'
import { LiveReports } from './live/LiveReports'
import { AreaSecureScore } from './live/SecureScoreGauge'
import { AreaExposureScore } from './live/ExposureScoreGauge'
import { referenceOf } from '../lib/api'
import type { FindingSummary } from '../lib/api'
import { AI_ASSETS_ITEM, AI_ITEM, CLOUD_ITEM, NAV_DESTINATIONS } from './tenants'
import { AzureTile, CloudArea } from './live/CloudArea'
import { AiArea } from './live/AiArea'
import { AiAssets } from './live/AiAssets'
import { backLabel, backTarget } from './backLabel'

/**
 * Every shell destination, until each has a page of its own.
 *
 * Deliberately says the page is not built rather than showing an empty card or a plausible
 * skeleton. A blank dashboard in a security product reads as "no findings", which is the most
 * dangerous thing an unbuilt screen could imply.
 *
 * Dashboard and Recommendations are no longer that: both read the real API now, and both render
 * the scan's freshness before anything derived from it. The fixture versions still exist under
 * /prototype in development, where nobody can mistake them for a tenant's own numbers.
 */
export function ShellPlaceholder() {
  const { pathname } = useLocation()

  const navigate = useNavigate()
  const location = useLocation()

  // A finding has a URL now. It was local state, with a note saying a deep link was a thing to
  // design rather than acquire by accident -- so here it is, designed. What it buys is the whole
  // point of printing a reference at all: the address bar carries the finding somebody is looking
  // at, so it can be sent to a colleague, bookmarked, reopened after a refresh, and left with the
  // back button. Until now every one of those landed the reader back on a list.
  //
  // The URL carries the REFERENCE, not the whole id -- the same eight characters the page shows
  // and the search box accepts, so what is on screen, what is in the url and what a customer
  // quotes are one string. Isolation is unchanged: the server resolves it under the caller's
  // capability, so a reference from another tenant is not forbidden, it is absent.
  const { reference } = useParams()

  const openFinding = (finding: FindingSummary) =>
    navigate(`/findings/${referenceOf(finding.id)}`, {
      // The clicked row, so the heading paints immediately instead of flashing "Loading…" for a
      // finding the reader was just looking at. Only ever an optimisation -- a link opened from
      // an email arrives with no state at all, which is the case the detail page is built for.
      // `from` keeps the query too: the list's filters live in it (?area=Email&owner=none), and Back
      // should land on the list as it was narrowed, not on the whole of it.
      state: { finding, from: location.pathname + location.search },
    })

  const page =
    [...NAV_DESTINATIONS, CLOUD_ITEM, AI_ITEM, AI_ASSETS_ITEM].find((item) => item.path === pathname)?.label ??
    'Dashboard'

  if (reference) {
    const known = (location.state as { finding?: FindingSummary } | null)?.finding
    const back = backTarget((location.state as { from?: unknown } | null)?.from)

    return (
      // The reference in the breadcrumb, because "Finding" alone named no finding -- the one
      // thing at the top of the page could not say which page it was.
      <AppShell page={`Finding ${reference}`}>
        <LiveFindingDetail
          reference={reference}
          finding={known}
          // Back to wherever this was opened from, or to the full list when it was opened from a
          // link. navigate(-1) would be wrong for the link case: it leaves the product.
          onBack={() => navigate(back)}
          // And SAYS where: "Back to dashboard", "Back to Email". It said "Back to findings" from
          // every page, including the dashboard it then returned to (review, 2026-09-27).
          backLabel={backLabel(back)}
        />
      </AppShell>
    )
  }

  if (pathname === '/dashboard') {
    // The same open-a-finding state the other two branches use. The dashboard now names findings
    // -- three titles inside each severity card, five in "fix these first" -- and a page that
    // lists a customer's worst problems and cannot open any of them sends them to hunt for the
    // same row on another screen.
    return (
      <AppShell page="Dashboard">
        <DashboardSwitch onOpen={openFinding} />
      </AppShell>
    )
  }

  // The posture pages: the same list, restricted to one area.
  //
  // These were placeholders reading "no tenant has been scanned", which stopped being true the
  // moment a scan ran and became actively false once Endpoint had a finding of its own — a
  // customer clicked Endpoint, saw a page saying nothing had been assessed, and was looking at
  // the one area that had just produced a High.
  //
  // The area names are the check's own Area strings, which are what the catalogue's domain
  // column holds and what the server filters on. Not the nav label: they agree today and a page
  // that silently shows everything the moment they stop agreeing is worse than one that shows
  // nothing.
  // Only the two that have a domain behind them. The catalogue holds Identity, Endpoint, Data,
  // Cloud, Email and AI; there is no Compliance domain, so pointing that page at Cloud would put
  // cloud findings under a heading meaning something else and call it done. Compliance stays a
  // placeholder until somebody decides what it is -- a page that shows the wrong findings is
  // worse than a page that admits it is not built.
  const AREA_FOR_PATH: Record<string, string> = {
    '/identity': 'Identity',
    '/endpoint': 'Endpoint',
    '/data': 'Data',

    // EMAIL GOT ITS COLLECTOR, so it gets its page. It was absent here for the reason AI still is:
    // a catalogue row and no check, so the page would have rendered "no open findings" about an
    // area nothing had ever assessed -- the flattering zero this product exists to avoid. Eleven
    // Exchange and Defender controls now run against a real tenant, so the zero would be earned
    // rather than empty, and on the first tenant assessed it was not a zero at all.
    //
    // Earned only where the tenant HAS Exchange Online. On a tenant that holds no licence for it,
    // and whose Exchange Microsoft refused to read, nothing in Email is assessed (0158, owner
    // 2026-09-26); the list says "Email is not assessed: this tenant has no Exchange Online
    // licence" rather than "No open findings in Email". That decision lives in LiveFindings, which
    // reads it from the scan's freshness, so every area page here gets it without a branch of its
    // own -- Data (SharePoint and OneDrive) and Endpoint (Defender, beside Intune) included.
    '/email': 'Email',

    // CLOUD (Azure CSPM, 2026-10-10): Prowler's Azure checks. Rendered through CloudArea, which
    // sends the page away where Azure is not assessed and says "part of Monitor" off Monitor,
    // rather than a list whose zero nobody measured.
    '/cloud': 'Cloud',

    // AI (2026-10-11): Azure AI resources, agents and AI applications, through AiArea, which also lists the inventory.
    '/ai': 'AI',

    // AI is still absent, and for exactly the reason Email used to be.
    //
    // Compliance is absent for the opposite reason: it has a nav item and no domain behind it at
    // all. Pointing it at some other area's findings would put them under a heading that means
    // something else.
  }

  const area = AREA_FOR_PATH[pathname]

  // KEYED ON THE AREA, below. Without a key, moving between two pages that both render
  // LiveFindings keeps the same React instance -- and with it the rows already loaded by "Load
  // more", which belong to the area just left. A SharePoint finding appeared under Identity for
  // exactly that reason: the server filtered correctly, the client asked correctly, and stale rows
  // sat in component state underneath the correct answer.
  //
  // A key rather than clearing state when the area changes, because it resets every piece of that
  // component's state at once -- loaded rows, cursor, request generation, in-flight lock -- and
  // cannot be partially forgotten the next time one is added.
  if (area) {
    return (
      <AppShell page={page}>
        {/* Identity only (owner, 2026-09-25): Microsoft breaks its Secure Score out by category,
            and its Identity category is Entra ID -- this page. Its other categories are not known
            to mean what our areas of similar names mean, and a category under the wrong heading
            is a number that says something else. Renders nothing if the overview cannot be read;
            the list is this page. */}
        {/* The area's own Microsoft figure now sits in the insights strip as its last tile. */}
        {/* Endpoint (owner, 2026-09-25): Microsoft Defender's exposure score, and the line saying
            how many vulnerabilities have no known exploit and so are not findings (0153). */}
        {area === 'Cloud'
          ? <CloudArea><LiveFindings key={area} onOpen={openFinding} area={area} areaTile={<AzureTile />} /></CloudArea>
          : area === 'AI'
            ? <AiArea><LiveFindings key={area} onOpen={openFinding} area={area} /></AiArea>
            : (
              <LiveFindings
                key={area}
                onOpen={openFinding}
                area={area}
                areaTile={
                  area === 'Identity' ? <AreaSecureScore category="Identity" compact />
                    : area === 'Endpoint' ? <AreaExposureScore compact />
                      : undefined
                }
              />
            )}
      </AppShell>
    )
  }

  // The three that were placeholders. Each reads the workspace's own row rather than describing
  // a product in general -- and each says plainly where a control does not exist yet instead of
  // rendering one that does nothing.
  if (pathname === '/settings' || pathname.startsWith('/settings/')) {
    const tab = settingsTabFor(pathname, location.search, location.hash)
    return (
      <AppShell page="Settings" subpage={[...SETTINGS_TABS, AZURE_TAB, AI_TAB].find((t) => t.id === tab)?.label}>
        <LiveSettings tab={tab} />
      </AppShell>
    )
  }

  if (pathname === '/plans') {
    return (
      <AppShell page="Plans">
        <LivePlans />
      </AppShell>
    )
  }

  if (pathname === '/reports') {
    return (
      <AppShell page="Reports">
        <LiveReports />
      </AppShell>
    )
  }

  if (pathname === '/ai-assets') {
    return (
      <AppShell page="AI assets">
        <AiAssets />
      </AppShell>
    )
  }

  if (pathname === '/recommendations') {
    return (
      <AppShell page="Recommendations">
        <LiveFindings key="all" onOpen={openFinding} />
      </AppShell>
    )
  }

  return (
    <AppShell page={page}>
      <h2>{page}</h2>
      <p className="text-muted" style={{ fontSize: 15, maxWidth: '60ch' }}>
        This page is not built yet. The shell around it is — the app bar, tenant switcher,
        navigation, breadcrumb and content card are the real components, and the page will
        render inside this card.
      </p>
      <p className="text-muted" style={{ fontSize: 13, maxWidth: '60ch' }}>
        Your tenant may well have been scanned — this page just has nothing to show you yet. What
        was found is on the dashboard and under Recommendations.
      </p>
    </AppShell>
  )
}
